import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { currentServiceTypeIsOvernight } from '@providers/booking/helper'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { DateSelector } from './date_selector'
import { RoomsAndPets } from './rooms_and_pets/rooms_and_pets'
import { getReservationPetIds } from '@components/double_booking/helper'
import { BookingConflictModal } from '@components/double_booking/booking_conflict_modal'
import { buildOvernightVisitPeriod } from '@components/double_booking/conflict_check'
import { useBookingConflictModal } from '@components/double_booking/use_booking_conflict_modal'

export const SearchBar = ({ isLoading }: { isLoading: boolean }) => {
  const theme = useTheme()
  const bookingCtx = useBookingContext()
  const { accountName: locationName = '', serviceTypeName = '' } = useParams()
  const { navigateRoute } = useRoutes()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const isOvernight = currentServiceTypeIsOvernight(bookingCtx.state)
  const conflict = useBookingConflictModal(locationName)

  const proceedWithReservation = () => {
    bookingCtx.dispatch({ type: 'REGROUP_PETS' })
    const targetState = serviceTypeName === 'grooming' ? 'searchResultsGrooming' : 'searchResults'
    bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: targetState } })
    navigateRoute('searchOffersResults', { serviceTypeName: serviceTypeName })
  }
  const doSearch = async () => {
    if (isOvernight) {
      const period = buildOvernightVisitPeriod(bookingCtx.state.startDate!, bookingCtx.state.endDate!)
      const petInvoiceId = getReservationPetIds(bookingCtx.state)
      const conf = await conflict.checkVisitConflicts({ period, locationPetProfileIds: petInvoiceId })
      if (conf) {
        proceedWithReservation()
      }
      return
    }
    proceedWithReservation()
  }

  return (
    <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
      <Paper elevation={1} sx={{ pt: 2, pb: 2, px: 4, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Stack direction={{ sm: 'column', md: 'row' }} spacing={2}>
          {isOvernight && (
            <>
              <DateSelector />
              {isBelowMD ? (
                <Divider orientation="horizontal" flexItem sx={{ pt: 1, mb: 1 }} />
              ) : (
                <Divider orientation="vertical" flexItem />
              )}
            </>
          )}
          {isLoading ? <Skeleton variant="rounded" height={45} width="100%" /> : <RoomsAndPets />}
          <Stack
            direction="column"
            alignItems={{ xs: 'start', md: 'end' }}
            justifyContent="center"
            sx={{ marginRight: 'auto', flexShrink: 1, mt: { xs: 2, md: 0 } }}
          >
            <Button size="large" variant="contained" disabled={!bookingCtx.state.isValid} onClick={doSearch}>
              {t('global.label.Search')}
            </Button>
          </Stack>
          <BookingConflictModal
            invoices={conflict.conflictInvoices}
            locationPetProfileIds={conflict.locationPetProfileIds}
            onClose={conflict.close}
            onBookAnyway={() => proceedWithReservation()}
            onReviewDates={conflict.close}
            isOpen={conflict.isOpen}
          />
        </Stack>
      </Paper>
    </Box>
  )
}
